import { useContext, useState } from "react";
import { getCellWidthForZoom, ToDateString } from "../../../lib/DateUtils";
import { plannerIcons } from "../../default_values/DefaultProjectIcons";
import moment from "moment";
import {
  useBookingsEditingStore,
  useCopyBookingsStore,
  useShiftPressedStore,
  useFilteredUsersStore,
  useZoomStore,
  useSelectedProjectStore,
} from "../../../stores/planner";
import { PlannerContext } from "../context/PlannerContext";
import lsKeys from "../../default_values/defaultKeys";
import { DraggableBooking } from "./DraggableBooking";
import {
  DAY_VIEW_HEIGHT,
  DAY_VIEW_WIDTH,
  getPlannerOffsetX,
  getPlannerOffsetY,
  formatMapping,
} from "../../../lib/PlannerUtils";
import { DateTime } from "luxon";
import { useProfileStore } from "../../../stores/profileStore";
import { shallow } from "zustand/shallow";
import { TeambookIcon } from "../../default_images/TeambookIcon";
import { icons } from "../../default_images/IconsList";
import { bookingStartTimeText } from "./planner_right_side_modal/components/StartTimeBlock";
import PropTypes from "prop-types";
import { SliceStringWithDots } from "../../../lib/StringFormat";

const UserBookings = ({
  bookingsArray,
  date,
  topOffset,
  weekendsHidden,
  maxHeight,
  selectBooking,
  activeProjects,
  changeTooltip,
  bookingFormat,
  teams,
  changeContextMenu,
  updateBookingsRequest,
  user,
  mouseMoved,
  mouseUped,
  mouseDowned,
  tasks,
  weekendInfoRef,
  index,
  closeCreationWindow,
  project,
  userBookingsHelper,
  page = "default",
}) => {
  const { team, projects } = useContext(PlannerContext);

  const bookingIds = useBookingsEditingStore(({ bookingsEditing }) => bookingsEditing.map((b) => b.id));
  const [currentUser] = useProfileStore((state) => [state.profile], shallow);
  const [zoom] = useZoomStore((state) => [state.zoom], shallow);
  const [clearBookingsFromEdit, setBookings, setEditWindowOpened] = useBookingsEditingStore(
    (state) => [state.clearBookings, state.setBookings, state.setEditWindowOpened],
    shallow
  );
  const [setSelectedProject, setSelectedTimeOff] = useSelectedProjectStore(
    (state) => [state.setSelectedProject, state.setSelectedTimeOff],
    shallow
  );

  const role = localStorage.getItem(lsKeys.ROLE);

  const [initialSize, setInitialSize] = useState(null);
  const [initialPos, setInitialPos] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const openWeekendInfo = (e, weekendDate, userId) => {
    weekendInfoRef.current.setPos({
      x: e.pageX,
      y: e.pageY,
    });

    weekendInfoRef.current.setOpen(true);
    weekendInfoRef.current.setUserIndex(index);
    weekendInfoRef.current.setUserId(userId);

    if (new Date(weekendDate).getDay() === 0) {
      weekendInfoRef.current.setDates([
        DateTime.fromISO(weekendDate).minus({ day: 1 }).toISODate(),
        DateTime.fromISO(weekendDate).toISODate(),
      ]);
    } else {
      weekendInfoRef.current.setDates([
        DateTime.fromISO(weekendDate).toISODate(),
        DateTime.fromISO(weekendDate).plus({ day: 1 }).toISODate(),
      ]);
    }
  };

  const numRound = (num, precision) => {
    return Math.round(num / precision) * precision;
  };

  const checkSelect = (booking) => bookingIds.includes(booking.id);

  const startBookingResizing = (e, booking) => {
    userBookingsHelper.startBookingResizing(e, booking, setIsDragging, setInitialPos, setInitialSize, project);
  };

  const resizingBooking = (e, booking) => {
    userBookingsHelper.resizingBooking(e, booking, initialSize, numRound, initialPos, bookingFormat, calculateHeight);
  };

  const endBookingResizing = (e, booking) => {
    userBookingsHelper.endBookingResizing(
      e,
      booking,
      numRound,
      bookingFormat,
      updateBookingsRequest,
      bookingsArray,
      getProject,
      setIsDragging
    );
  };

  const isResizable = (booking) => {
    if (!bookingIds.length) {
      return true;
    } else {
      return bookingIds.length === 1 && bookingIds[0] === booking.id;
    }
  };

  const calculateLeftOffset = (bookingDate, startTime) => {
    return userBookingsHelper.calculateLeftOffset(bookingDate, date, weekendsHidden, zoom, user, startTime);
  };

  const calculateHeight = (bookingDuration, bookingId, bookingDate) => {
    if (zoom === 1) {
      return DAY_VIEW_HEIGHT;
    }

    let sameDateBookings = getSameDateBookings(bookingDate);

    let index = sameDateBookings.findIndex((booking) => booking.id === bookingId);
    let emptySpace = maxHeight;

    for (let i = 0; i < index; i++) {
      emptySpace -= (sameDateBookings[i].duration / 60) * formatMapping[bookingFormat];
    }

    emptySpace = emptySpace > 0 ? emptySpace : 0;

    let calculatedHeight = (bookingDuration / 60) * formatMapping[bookingFormat];

    return calculatedHeight <= emptySpace ? calculatedHeight : emptySpace;
  };

  const calculateOffsetTop = (id, date) => {
    if (zoom === 1) {
      return topOffset;
    }

    let offset = 0;
    let sameDateBookings = getSameDateBookings(date);

    let index = sameDateBookings.findIndex((booking) => booking.id === id);

    for (let i = 0; i < index; i++) {
      offset += calculateHeight(sameDateBookings[i].duration);
    }

    return topOffset + offset;
  };

  const bookingIsNotTimeOff = (booking) => {
    return getProject(booking.project_id)?.kind !== "time_off";
  };

  const getSameDateBookings = (date) => {
    const datedBookings = bookingsArray
      .filter((booking) => booking.date === date)
      .filter((booking) => booking.deleted_at === null);

    const startTimeBookings = datedBookings
      .filter((booking) => booking.start_time !== null)
      .sort((a, b) => {
        return a.start_time - b.start_time;
      });
    const otherBookings = datedBookings
      .filter((booking) => booking.start_time === null)
      .sort((a, b) => {
        const orderDifference = a.order - b.order;

        if (orderDifference === 0) {
          return a.id - b.id;
        } else {
          return orderDifference;
        }
      });

    return [...startTimeBookings, ...otherBookings];
  };

  const calculateWidth = (booking) => {
    const { duration, date } = booking;

    if (zoom === 1) {
      return (duration / 60) * DAY_VIEW_WIDTH;
    } else {
      const cellWidth = getCellWidthForZoom(zoom) - (DateTime.fromISO(date).weekday === 5 ? 0 : 1);
      let groupingDays = 1;

      if (booking.grouping_id) {
        groupingDays =
          bookingsArray.filter(
            (b) =>
              b.grouping_id &&
              b.grouping_id === booking.grouping_id &&
              (!weekendsHidden || DateTime.fromISO(b.date).weekday < 6)
          ).length || 1;
      }

      return cellWidth * groupingDays + (groupingDays - 1);
    }
  };

  const projectCode = (booking) => {
    if (project && project.id !== booking.project_id) {
      return <TeambookIcon name={icons.BOOKING_BLOCK} />;
    }

    return getProject(booking.project_id)?.code?.slice(0, 5);
  };

  const createGradient = (primary, secondary) => {
    return `repeating-linear-gradient(
        -45deg,
        ${primary},
        ${primary} 10px,
        ${secondary} 10px,
        ${secondary} 20px
      )`;
  };

  const getBackground = (booking) => {
    const baseColor = booking.project_color;
    const tentativeColor = "var(--background-1)";
    const color = booking.tentative ? tentativeColor : `${baseColor}66`;

    if (project && project.id !== booking.project_id && bookingIsNotTimeOff(booking)) {
      return "#9D9D9D99";
    }

    if (team.id === booking.team_id) {
      return `linear-gradient(${color}, ${color}), var(--background-1)`;
    } else {
      return `${createGradient(color, `${baseColor}4D`)}, var(--background-1)`;
    }
  };

  const getLineColor = (booking) => {
    if (project && project.id !== booking.project_id) {
      return "#9D9D9D";
    }
    if (team.id !== booking.team_id) {
      return `${booking.project_color}`;
    }
    return `${booking.project_color}`;
  };

  const getBoxShadow = (booking) => {
    if (getProject(booking.project_id)?.kind === "time_off" && booking.tentative) {
      return `0 0 0 2px ${booking.project_color} inset`;
    }

    if (project && project.id !== booking.project_id) {
      return null;
    }

    if (team.id !== booking.team_id) {
      return booking.tentative ? `0 0 0 2px ${booking.project_color} inset` : null;
    }

    return booking.tentative ? `0 0 0 2px ${booking.project_color} inset` : "";
  };

  const getProject = (id) => {
    if (activeProjects.find((pr) => pr.id === id) === undefined) {
      return projects.find((pr) => pr.id === id);
    }

    return activeProjects.find((pr) => pr.id === id);
  };

  const getColor = () => {
    const isDarkTheme = localStorage.getItem(lsKeys.THEME) === "dark";

    if (isDarkTheme) {
      return "#FFFFFF";
    } else {
      return "#474747";
    }
  };

  const getOpacity = (booking) => {
    const { date, grouping_id } = booking;
    const yesterdayDate = new Date() - 86400000;
    const bookingDate = new Date(date);

    if (grouping_id && yesterdayDate > bookingDate) {
      return bookingsArray.filter((b) => b.grouping_id === grouping_id && new Date(b.date) > yesterdayDate).length > 0
        ? "1"
        : "0.65";
    } else {
      return yesterdayDate > bookingDate ? "0.65" : "1";
    }
  };

  const dateIsNotWeekend = (date) => {
    if (!weekendsHidden || !date || zoom === 1) {
      return true;
    }

    return moment(date).day() !== 0 && moment(date).day() !== 6;
  };

  const bookingObjectStyles = (booking) => {
    return {
      width: `${calculateWidth(booking)}px`,
      height: `${calculateHeight(booking.duration, booking.id, booking.date) - 1}px`,
      top: 0,
      background: getBackground(booking),
      boxShadow: getBoxShadow(booking),
      color: getColor(booking),
      fontWeight: "700",
      display:
        calculateHeight(booking.duration, booking.id, booking.date) === 0 || booking.deleted_at !== null
          ? "none"
          : "block",
      opacity: getOpacity(booking),
      padding: calculateBookingPadding(booking),
      cursor: booking.team_id === team.id ? "grab" : "not-allowed",
      transform: `translate3d(${calculateLeftOffset(booking.date, booking.start_time)}px, ${calculateOffsetTop(
        booking.id,
        booking.date,
        booking.order
      )}px, 0)`,
    };
  };

  const calculateBookingPadding = (booking) => {
    if (zoom >= 90) {
      return "1px 0px 0px 0px";
    } else {
      if (booking.duration <= 60) {
        return "0px 2px 2px 6px";
      } else {
        return "2px 2px 2px 6px";
      }
    }
  };

  const getCommentText = (comment) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = comment;
    return tempDiv.textContent || tempDiv.innerText;
  };

  const weekendBooking = bookingsArray.find(
    (booking) =>
      bookingIsNotTimeOff(booking) &&
      (moment(booking.date).day() === 0 || moment(booking.date).day() === 6) &&
      booking.deleted_at === null
  );

  const bookingShouldBeShown = (booking) => {
    // If zoom = 1, we show only booking for 1 day, which is `date`
    if (zoom === 1 && booking.date !== ToDateString(date)) {
      return false;
    }

    // If zoom is not 1,
    if (zoom !== 1 && slavedInGroup(booking)) {
      return false;
    }

    return true;
  };

  const slavedInGroup = (booking) => {
    if (booking.grouping_id === null) {
      return false;
    }

    return bookingsArray.some(
      (b) => b.grouping_id === booking.grouping_id && DateTime.fromISO(b.date) < DateTime.fromISO(booking.date)
    );
  };

  const getHTMLBookingId = (booking) => {
    if (page === "grouped") {
      return `booking-${booking.id}-${project.id}`;
    }

    return `booking-${booking.id}`;
  };

  return (
    <>
      {bookingsArray
        .filter((b) => bookingShouldBeShown(b))
        .map(
          (booking) =>
            dateIsNotWeekend(booking.date) && (
              <DraggableBooking
                isDragging={
                  ["regular", "contractor"].includes(role) ||
                  (role === "self_planner" && booking.user_id !== currentUser.id) ||
                  ["google_calendar", "outlook_calendar"].includes(booking.created_with)
                    ? true
                    : isDragging
                }
                key={"drag" + booking.id + "/" + booking.duration}
                booking={booking}
                canDrag={team.id === booking.team_id}
              >
                <div
                  key={`booking-${booking.id}`}
                  id={getHTMLBookingId(booking)}
                  aria-label="booking"
                  className={`proj-${booking.project_id} booking-${booking.id}-${booking.project_id} booking`}
                  data-active={bookingIds.includes(booking.id)}
                  style={{
                    ...bookingObjectStyles(booking),
                    position: "relative",
                  }}
                  onClick={() => {
                    if (booking.grouping_id) {
                      bookingsArray
                        .filter((b) => b.grouping_id === booking.grouping_id)
                        .forEach((b) => {
                          clearBookingsFromEdit();
                          setBookings(bookingsArray.filter((booking) => b.grouping_id === booking.grouping_id));
                          setEditWindowOpened(true);

                          closeCreationWindow();
                        });

                      const project = projects.filter((pr) => pr.id === booking.project_id)[0];

                      if (project) {
                        if (project.kind === "time_off") {
                          setSelectedTimeOff(project);
                        } else {
                          setSelectedProject(project);
                        }
                      } else {
                        setSelectedProject(projects.filter((pr) => pr.kind !== "time_off")?.[0]);
                      }
                    } else {
                      selectBooking(booking);
                    }
                  }}
                  onMouseLeave={() => {
                    changeTooltip({ open: false });
                  }}
                  onMouseDown={(e) => {
                    if (
                      useShiftPressedStore.getState().shiftPressed ||
                      (useCopyBookingsStore.getState().copyModeActive &&
                        useCopyBookingsStore.getState().copyBookingIds.length > 0)
                    ) {
                      e.preventDefault();
                      mouseDowned(
                        getPlannerOffsetX(e),
                        getPlannerOffsetY(e),
                        useFilteredUsersStore.getState().filteredUsers
                      );
                    }
                  }}
                  onMouseUp={(e) => {
                    if (
                      useShiftPressedStore.getState().shiftPressed ||
                      useCopyBookingsStore.getState().copyBookingIds.length > 0
                    ) {
                      mouseUped(
                        getPlannerOffsetX(e),
                        getPlannerOffsetY(e),
                        useFilteredUsersStore.getState().filteredUsers,
                        e
                      );
                    }
                  }}
                  onMouseMove={(e) => {
                    if (useShiftPressedStore.getState().shiftPressed) {
                      mouseMoved(
                        getPlannerOffsetX(e),
                        getPlannerOffsetY(e),
                        useFilteredUsersStore.getState().filteredUsers,
                        e
                      );
                    }

                    changeTooltip({
                      open: true,
                      projectName: getProject(booking.project_id)?.name,
                      clientName: getProject(booking.project_id)?.client_name,
                      bookingDuration: booking.duration,
                      comment: booking.comment,
                      team: teams.find((team) => team.id === booking.team_id),
                      task: tasks.filter((t) => t.id === booking.task_id)?.[0],
                      left: e.currentTarget.getBoundingClientRect().x,
                      top: e.currentTarget.getBoundingClientRect().y,
                    });
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    if (
                      ["admin", "planner"].includes(role) ||
                      (["self_planner"].includes(role) && currentUser.id === booking.user_id) ||
                      ["google_calendar", "outlook_calendar"].includes(booking.created_with)
                    ) {
                      changeTooltip({
                        open: false,
                      });
                      changeContextMenu({
                        open: true,
                        posY: e.pageY,
                        posX: e.pageX,
                        bookings: booking.grouping_id
                          ? bookingsArray.filter((b) => b.grouping_id === booking.grouping_id)
                          : [booking],
                      });
                    }

                    e.stopPropagation();
                  }}
                >
                  {!booking.tentative && (
                    <div
                      className="booking-line"
                      style={{
                        backgroundColor: getLineColor(booking),
                        width:
                          zoom === 90 ? (checkSelect(booking) ? "1px" : "2px") : checkSelect(booking) ? "0px" : "4px",
                      }}
                    />
                  )}

                  {booking.duration > 60 &&
                    !(role === "self_planner" && user.id !== currentUser.id) &&
                    !["regular", "contractor"].includes(role) &&
                    !["google_calendar", "outlook_calendar"].includes(booking.created_with) && (
                      <div
                        id={`resizer-${booking.id}`}
                        className="booking__resizer"
                        style={{
                          display: isResizable(booking) ? "block" : "none",
                        }}
                        onDragStart={(e) => startBookingResizing(e, booking)}
                        onDrag={(e) => resizingBooking(e, booking)}
                        onDragEnd={(e) => endBookingResizing(e, booking)}
                        draggable={true}
                      />
                    )}

                  {zoom < 90 && (
                    <>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          height: "17px",
                        }}
                      >
                        {zoom === 14 ? (
                          <p className="booking__project-name">{getProject(booking.project_id)?.name}</p>
                        ) : (
                          <p className="booking__project-code">{projectCode(booking)}</p>
                        )}
                      </div>

                      {booking.task_id && (
                        <div
                          style={{
                            height: "17px",
                          }}
                        >
                          <p className="booking__task-name">
                            {tasks.filter((t) => t.id === booking.task_id)?.[0]?.name}
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {getProject(booking.project_id) && (
                    <>
                      <div
                        style={{
                          gap: zoom >= 90 && "2px",
                          flexDirection: zoom >= 90 ? "column" : "row",
                          width: zoom >= 90 && booking.grouping_id && "19px",
                        }}
                        className="booking__booking-icon"
                      >
                        {getProject(booking.project_id)?.kind === "time_off" &&
                          plannerIcons(getProject(booking.project_id).icon_id, getColor(), zoom)}

                        {booking.repetition && <TeambookIcon name={icons.BOOKING_REPETITION} />}

                        {booking.location === 1 && <TeambookIcon name={icons.BOOKING_ON_CLIENT_SITE} />}

                        {booking.comment.length > 0 && <TeambookIcon name={icons.BOOKING_COMMENT} />}

                        {booking.location === 2 && <TeambookIcon name={icons.BOOKING_AT_HOME} />}

                        {booking.using_budget === false && <TeambookIcon name={icons.BOOKING_NON_BILLABLE} />}
                      </div>

                      {booking.start_time?.toString() && (
                        <div
                          className="booking__start-time-block"
                          style={{
                            display: "flex",
                            justifyContent: zoom >= 90 && "center",
                          }}
                        >
                          {zoom < 90 && (
                            <p
                              className="booking__start-time-text"
                              style={{
                                color: getColor(booking),
                              }}
                            >
                              {bookingStartTimeText(booking)}
                            </p>
                          )}
                        </div>
                      )}

                      {zoom === 14 && booking.comment.length > 0 && (
                        <p className="booking__comment">{SliceStringWithDots(getCommentText(booking.comment), 20)}</p>
                      )}
                    </>
                  )}
                </div>
              </DraggableBooking>
            )
        )}
      {weekendBooking && weekendsHidden && (
        <TeambookIcon
          name={icons.WARNING}
          color="common"
          className="booking__weekend-booking-sign pointer"
          style={{
            left: `${calculateLeftOffset(weekendBooking.date, weekendBooking.start_time) + 3}px`,
          }}
          key={`weekend-warning-${weekendBooking.date}`}
          onClick={(e) => {
            openWeekendInfo(e, weekendBooking.date, weekendBooking.user_id);
          }}
        />
      )}
    </>
  );
};

UserBookings.propTypes = {
  bookingsArray: PropTypes.arrayOf(PropTypes.object).isRequired,
  date: PropTypes.instanceOf(Date).isRequired,
  topOffset: PropTypes.number.isRequired,
  weekendsHidden: PropTypes.bool.isRequired,
  maxHeight: PropTypes.number.isRequired,
  selectBooking: PropTypes.func.isRequired,
  activeProjects: PropTypes.arrayOf(PropTypes.object).isRequired,
  changeTooltip: PropTypes.func.isRequired,
  bookingFormat: PropTypes.any.isRequired,
  teams: PropTypes.arrayOf(PropTypes.object).isRequired,
  changeContextMenu: PropTypes.func.isRequired,
  updateBookingsRequest: PropTypes.func.isRequired,
  user: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
  mouseMoved: PropTypes.func.isRequired,
  mouseUped: PropTypes.func.isRequired,
  mouseDowned: PropTypes.func.isRequired,
  tasks: PropTypes.arrayOf(PropTypes.object).isRequired,
  weekendInfoRef: PropTypes.shape({
    current: PropTypes.shape({
      setPos: PropTypes.func,
      setOpen: PropTypes.func,
      setUserIndex: PropTypes.func,
      setUserId: PropTypes.func,
      setDates: PropTypes.func,
    }),
  }).isRequired,
  index: PropTypes.number.isRequired,
  closeCreationWindow: PropTypes.func.isRequired,
  project: PropTypes.shape({
    id: PropTypes.number,
    kind: PropTypes.string,
  }),
  userBookingsHelper: PropTypes.shape({
    startBookingResizing: PropTypes.func.isRequired,
    resizingBooking: PropTypes.func.isRequired,
    endBookingResizing: PropTypes.func.isRequired,
    calculateLeftOffset: PropTypes.func.isRequired,
  }).isRequired,
  page: PropTypes.string,
};

export default UserBookings;
